import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Search, Trash2, ChevronRight, Sparkles } from "lucide-react";

export default function History() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/prompts", { params: query ? { q: query } : {} });
      setItems(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    try {
      await api.delete(`/prompts/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      setConfirmId(null);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setConfirmId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">Arkib</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">Sejarah Prompt</h1>
        </div>
        <Link to="/generator" className="btn-primary inline-flex items-center gap-2" data-testid="new-prompt-btn">
          <Sparkles size={16} /> Baru
        </Link>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
          placeholder="Cari (poster type, kandungan, nota)…"
          className="w-full pl-12 pr-4 py-3 bg-[#101010] border border-white/10 rounded-md text-white font-mono text-sm focus:border-amber-500/40 focus:outline-none"
          data-testid="history-search-input"
        />
      </div>

      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-neutral-500 text-center py-12">Memuatkan…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="history-empty">
          <p className="text-neutral-400">Belum ada prompt dijana.</p>
          <Link to="/generator" className="text-amber-400 text-sm hover:underline mt-2 inline-block">
            Mula menjana →
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {items.map((p) => (
            <div
              key={p.id}
              className="group bg-[#101010] border border-white/10 hover:border-amber-500/40 rounded-lg p-5 transition-all"
              data-testid={`history-item-${p.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1 font-mono">
                    <span className="text-amber-500/80">{p.model}</span>
                    <span>·</span>
                    <span>{new Date(p.created_at).toLocaleString("ms-MY")}</span>
                    {p.image_count > 0 && (
                      <>
                        <span>·</span>
                        <span>{p.image_count} gambar</span>
                      </>
                    )}
                  </div>
                  <div className="font-display text-lg font-semibold">{p.poster_type}</div>
                  <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{p.prompt}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setConfirmId(p.id)}
                    className="p-2 rounded-md text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    data-testid={`history-delete-${p.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                  <Link
                    to={`/prompts/${p.id}`}
                    className="p-2 rounded-md text-neutral-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                    data-testid={`history-open-${p.id}`}
                  >
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmId && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmId(null)}
          data-testid="confirm-delete-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#101010] border border-red-500/30 rounded-xl p-6 w-full max-w-md shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <Trash2 className="text-red-400" size={18} />
              </div>
              <h3 className="font-display text-xl font-bold">Padam Prompt?</h3>
            </div>
            <p className="text-sm text-neutral-400 mb-5">
              Prompt ini akan dipadam kekal. Tindakan ini tidak boleh dibatalkan.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmId(null)}
                className="px-4 py-2 rounded-md text-neutral-300 hover:bg-white/5"
                data-testid="cancel-delete-btn"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => del(confirmId)}
                className="px-4 py-2 rounded-md bg-red-500 text-white font-semibold hover:bg-red-400 transition-colors"
                data-testid="confirm-delete-btn"
              >
                Ya, Padam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
